import { Controller } from "@hotwired/stimulus"

// Cover-letter builder modal. Portals to <body>, opens via [data-cover-letter-builder-open]
// markers (edit triggers add data-cover-letter-id). Source-Resume-driven: contact header +
// achievement chips come from the selected resume's embedded seed data. Live placeholder
// preview interpolates company/role + highlights selected chips; Generate/Regenerate produce
// AI versions (switchable); Save Draft PATCHes the active body.
export default class extends Controller {
  static targets = ["resumeSelect", "company", "role", "jobDescription", "tone", "length",
                    "chips", "customInput", "preview", "previewBody", "previewName", "previewContact",
                    "editText", "versionBar", "versionLabel", "generateBtn", "generateLabel",
                    "saveBtn", "error", "empty", "form"]
  static values = {
    resumes: Array, generateUrl: String, downloadUrl: String, showUrlTemplate: String
  }

  connect() {
    if (this.element.parentElement !== document.body) document.body.appendChild(this.element)
    this._open = (e) => this.open(e)
    document.querySelectorAll("[data-cover-letter-builder-open]").forEach((b) => b.addEventListener("click", this._open))
    this._onKey = (e) => { if (e.key === "Escape") this.close() }
    this.versions = []
    this.activeIndex = 0
    this.coverLetterId = ""
    this.selectedChips = new Set()
    const p = new URLSearchParams(window.location.search)
    if (p.get("new") === "1") this.open()
  }

  disconnect() {
    document.querySelectorAll("[data-cover-letter-builder-open]").forEach((b) => b.removeEventListener("click", this._open))
  }

  open(event) {
    const id = event && event.currentTarget && event.currentTarget.getAttribute("data-cover-letter-id")
    this.element.classList.remove("hidden"); this.element.classList.add("flex")
    document.addEventListener("keydown", this._onKey)
    this._resetState()
    this._syncResume()
    if (id) this._prefill(id)
    this._renderPreview()
  }

  close() {
    this.element.classList.add("hidden"); this.element.classList.remove("flex")
    document.removeEventListener("keydown", this._onKey)
  }
  backdrop(e) { if (e.target === this.element) this.close() }
  stop(e) { e.stopPropagation() }

  _resumeById(id) { return (this.resumesValue || []).find((r) => r.public_id === id) }
  currentResume() {
    const id = this.hasResumeSelectTarget ? this.resumeSelectTarget.value : ""
    return this._resumeById(id) || (this.resumesValue || [])[0]
  }

  changeResume() { this.selectedChips = new Set(); this._syncResume(); this._renderPreview() }

  _syncResume() {
    const r = this.currentResume()
    const hasResumes = (this.resumesValue || []).length > 0
    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle("hidden", hasResumes)
    if (this.hasFormTarget) this.formTarget.classList.toggle("hidden", !hasResumes)
    if (!r) return
    if (this.hasPreviewNameTarget) this.previewNameTarget.textContent = r.contact.name || "Your Name"
    if (this.hasPreviewContactTarget) {
      this.previewContactTarget.textContent = [r.contact.location, r.contact.email, r.contact.phone].filter(Boolean).join("  |  ")
    }
    this._renderChips(r.achievements || [])
  }

  _renderChips(seed) {
    if (!this.hasChipsTarget) return
    const labels = Array.from(new Set([...seed, ...Array.from(this.selectedChips)]))
    this.chipsTarget.innerHTML = labels.map((label) => {
      const on = this.selectedChips.has(label)
      const cls = on
        ? "bg-primary-container text-on-primary-container"
        : "bg-surface-container-high text-on-surface border border-border-light"
      return `<button type="button" data-action="cover-letter-builder#toggleChip" data-chip="${this._esc(label)}"
        class="inline-flex items-center gap-1 px-3 py-1 rounded-full font-label-sm text-label-sm ${cls}">
        ${this._esc(label)} <span class="material-symbols-outlined text-[16px]">${on ? "close" : "add"}</span></button>`
    }).join("")
  }

  toggleChip(e) {
    const label = e.currentTarget.getAttribute("data-chip")
    if (this.selectedChips.has(label)) this.selectedChips.delete(label); else this.selectedChips.add(label)
    this._renderChips(this.currentResume()?.achievements || [])
    this._renderPreview()
  }

  addCustom() {
    const v = (this.customInputTarget.value || "").trim()
    if (!v) return
    this.selectedChips.add(v); this.customInputTarget.value = ""
    this._renderChips(this.currentResume()?.achievements || [])
    this._renderPreview()
  }

  inputChanged() { this._renderPreview() }

  _renderPreview() {
    if (this.versions.length) return this._renderVersion()
    if (!this.hasPreviewBodyTarget) return
    const company = this._val(this.companyTarget) || "the company"
    const role = this._val(this.roleTarget) || "the role"
    const chips = Array.from(this.selectedChips)
    const hl = (t) => `<span class="bg-primary-container/30 px-1 rounded">${this._esc(t)}</span>`
    const achievementsLine = chips.length
      ? `In my recent work I ${chips.map(hl).join(", ")}.`
      : "My recent work spans the responsibilities this role calls for."
    this.previewBodyTarget.innerHTML = `
      <p>Dear Hiring Manager,</p>
      <p>I am writing to express my strong interest in the ${hl(role)} position at ${hl(company)}.</p>
      <p>${achievementsLine}</p>
      <p>Thank you for considering my application. I would welcome the opportunity to discuss how my background aligns with ${this._esc(company)}'s needs.</p>
      <p>Sincerely,<br>${this._esc(this.currentResume()?.contact?.name || "")}</p>`
  }

  _renderVersion() {
    const text = this.versions[this.activeIndex] || ""
    if (this.hasPreviewBodyTarget) {
      this.previewBodyTarget.innerHTML = text.split(/\n{2,}/).map((p) => `<p>${this._esc(p).replace(/\n/g, "<br>")}</p>`).join("")
    }
    if (this.hasEditTextTarget) this.editTextTarget.value = text
    if (this.hasVersionBarTarget) this.versionBarTarget.classList.toggle("hidden", this.versions.length < 2)
    if (this.hasVersionLabelTarget) this.versionLabelTarget.textContent = `Version ${this.activeIndex + 1} / ${this.versions.length}`
    if (this.hasSaveBtnTarget) this.saveBtnTarget.disabled = false
  }

  prevVersion() { if (this.activeIndex > 0) { this.activeIndex--; this._renderVersion() } }
  nextVersion() { if (this.activeIndex < this.versions.length - 1) { this.activeIndex++; this._renderVersion() } }

  toggleEdit() {
    if (!this.hasEditTextTarget || !this.hasPreviewBodyTarget) return
    const editing = this.editTextTarget.classList.toggle("hidden") === false
    this.previewBodyTarget.classList.toggle("hidden", editing)
  }
  editChanged() { if (this.versions.length) this.versions[this.activeIndex] = this.editTextTarget.value }

  generate() { this._generate(false) }
  regenerate() { this._generate(true) }

  async _generate(variation) {
    if (!this.currentResume()) return
    this._clearError(); this._busy(true)
    const fd = new FormData()
    fd.append("source_resume_public_id", this.resumeSelectTarget.value)
    fd.append("job_description", this._val(this.jobDescriptionTarget) || `${this._val(this.roleTarget)} at ${this._val(this.companyTarget)}`)
    fd.append("company_name", this._val(this.companyTarget))
    fd.append("target_role", this._val(this.roleTarget))
    fd.append("tone", this._val(this.toneTarget))
    fd.append("target_length", this._val(this.lengthTarget))
    Array.from(this.selectedChips).forEach((c) => fd.append("achievements[]", c))
    if (this.coverLetterId) fd.append("cover_letter_id", this.coverLetterId)
    if (variation) fd.append("variation", "true")
    try {
      const res = await fetch(this.generateUrlValue, {
        method: "POST", credentials: "same-origin",
        headers: { "Accept": "application/json", "X-CSRF-Token": this._csrf() }, body: fd
      })
      const data = await res.json().catch(() => ({}))
      if (data && data.success && data.cover_letter) {
        this.versions.push(data.cover_letter); this.activeIndex = this.versions.length - 1
        if (data.cover_letter_id) this.coverLetterId = data.cover_letter_id
        this._renderVersion()
      } else {
        this._error((data && data.error) || "Generation failed. Please try again.")
      }
    } catch (e) { this._error("Network error. Please try again.") }
    finally { this._busy(false) }
  }

  async save() {
    if (!this.coverLetterId || !this.versions.length) return this.close()
    const url = this.showUrlTemplateValue.replace(":id", this.coverLetterId)
    const fd = new FormData()
    fd.append("cover_letter[body]", this.versions[this.activeIndex])
    fd.append("cover_letter[company_name]", this._val(this.companyTarget))
    fd.append("cover_letter[target_role]", this._val(this.roleTarget))
    fd.append("cover_letter[tone]", this._val(this.toneTarget))
    // Persist the edited Job Description too — controller permits and
    // _prefill reads `job_description`, but it used to be silently
    // dropped on Save Draft, making target-job edits appear to save
    // and then revert on reload.
    if (this.hasJobDescriptionTarget) {
      fd.append("cover_letter[job_description]", this._val(this.jobDescriptionTarget))
    }
    fd.append("_method", "patch")
    try {
      await fetch(url, { method: "POST", credentials: "same-origin",
        headers: { "Accept": "application/json", "X-CSRF-Token": this._csrf() }, body: fd })
    } catch (e) { /* non-fatal */ }
    this._refreshAndClose()
  }

  download() {
    if (!this.versions.length) return
    const fd = new FormData()
    fd.append("cover_letter", this.versions[this.activeIndex]); fd.append("page_size", "A4")
    fetch(this.downloadUrlValue, { method: "POST", credentials: "same-origin",
      headers: { "X-CSRF-Token": this._csrf() }, body: fd })
      .then((r) => r.blob()).then((blob) => {
        const a = document.createElement("a"); a.href = URL.createObjectURL(blob)
        a.download = "cover-letter.pdf"; a.click(); URL.revokeObjectURL(a.href)
      }).catch(() => this._error("Download failed."))
  }

  async _prefill(id) {
    try {
      const res = await fetch(this.showUrlTemplateValue.replace(":id", id),
        { credentials: "same-origin", headers: { "Accept": "application/json" } })
      if (!res.ok) return
      const d = await res.json()
      this._set(this.companyTarget, d.company_name); this._set(this.roleTarget, d.target_role)
      this._set(this.jobDescriptionTarget, d.job_description); this._set(this.toneTarget, d.tone)
      if (d.body) { this.versions = [d.body]; this.activeIndex = 0; this.coverLetterId = d.public_id; this._renderVersion() }
    } catch (e) { /* show fresh */ }
  }

  _resetState() { this.versions = []; this.activeIndex = 0; this.coverLetterId = ""
    if (this.hasVersionBarTarget) this.versionBarTarget.classList.add("hidden")
    if (this.hasSaveBtnTarget) this.saveBtnTarget.disabled = true
    if (this.hasEditTextTarget) this.editTextTarget.classList.add("hidden")
    if (this.hasPreviewBodyTarget) this.previewBodyTarget.classList.remove("hidden")
    this._clearError() }
  _refreshAndClose() { this.close(); window.location.reload() }
  _val(t) { return t ? (t.value || "").trim() : "" }
  _set(t, v) { if (t) t.value = v || "" }
  _busy(on) {
    if (this.hasGenerateBtnTarget) this.generateBtnTarget.disabled = on
    if (this.hasGenerateLabelTarget) this.generateLabelTarget.textContent = on ? "Generating…" : "Generate with AI"
  }
  _csrf() { return document.querySelector('meta[name="csrf-token"]')?.content || "" }
  _esc(s) { const d = document.createElement("div"); d.textContent = s == null ? "" : String(s); return d.innerHTML }
  _error(m) { if (this.hasErrorTarget) { this.errorTarget.textContent = m; this.errorTarget.classList.remove("hidden") } }
  _clearError() { if (this.hasErrorTarget) this.errorTarget.classList.add("hidden") }
}
